import React, {
  FC,
  KeyboardEvent,
  memo,
  useCallback,
  useRef,
  useState,
} from "react";
import { ButtonColor } from "enums/Button";
import { IconType } from "enums/Icon";
import { useTranslation } from "react-i18next";
import Icon from "../../../../components/Icon/Icon";
import { ResetFilterButton } from "styles/searchFilterStyles";
import {
  HeaderWrapper,
  SearchButton,
  SearchGroup,
  SearchInputStyled,
  Wrapper,
} from "./HistoryHeader.styles";

interface HistoryHeaderProps {
  onSearch: (params: {
    trackingNumber?: string;
    consolidationSku?: string;
  }) => void;
}

const HistoryHeader: FC<HistoryHeaderProps> = ({ onSearch }) => {
  const { t } = useTranslation("common");
  const [trackingNumber, setTrackingNumber] = useState("");
  const [consolidationSku, setConsolidationSku] = useState("");
  const [isSearchActive, setIsSearchActive] = useState(false);
  const trackingRef = useRef<HTMLInputElement>(null);
  const skuRef = useRef<HTMLInputElement>(null);

  const handleSearch = useCallback(() => {
    const hasQuery = !!trackingNumber.trim() || !!consolidationSku.trim();
    if (hasQuery) {
      setIsSearchActive(true);
    }
    onSearch({
      trackingNumber: trackingNumber.trim() || undefined,
      consolidationSku: consolidationSku.trim() || undefined,
    });
  }, [onSearch, trackingNumber, consolidationSku]);

  const handleClear = useCallback(() => {
    setTrackingNumber("");
    setConsolidationSku("");
    setIsSearchActive(false);
    onSearch({});
  }, [onSearch]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Enter") {
        handleSearch();
      }
    },
    [handleSearch],
  );

  const isDirty = !!trackingNumber || !!consolidationSku;

  return (
    <Wrapper>
      <HeaderWrapper>
        <SearchGroup>
          <SearchInputStyled
            ref={trackingRef}
            value={trackingNumber}
            onChange={(e) => setTrackingNumber(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t("parcels.searchByTrackingNumber")}
          />
          <SearchButton
            title={trackingNumber ? t("common.clear") : t("common.search")}
            aria-label="search-tracking"
            onClick={trackingNumber ? () => { setTrackingNumber(""); if (!consolidationSku) { setIsSearchActive(false); onSearch({}); } } : handleSearch}
          >
            <Icon type={trackingNumber ? IconType.Close : IconType.Search} />
          </SearchButton>
        </SearchGroup>
        <SearchGroup>
          <SearchInputStyled
            ref={skuRef}
            value={consolidationSku}
            onChange={(e) => setConsolidationSku(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t("parcels.searchBySku")}
          />
          <SearchButton
            title={consolidationSku ? t("common.clear") : t("common.search")}
            aria-label="search-sku"
            onClick={consolidationSku ? () => { setConsolidationSku(""); if (!trackingNumber) { setIsSearchActive(false); onSearch({}); } } : handleSearch}
          >
            <Icon type={consolidationSku ? IconType.Close : IconType.Search} />
          </SearchButton>
        </SearchGroup>
        {isSearchActive && (
          <ResetFilterButton
            color={ButtonColor.Red}
            onClick={handleClear}
          >
            {t("parcels.search.resetFilter")}
          </ResetFilterButton>
        )}
      </HeaderWrapper>
    </Wrapper>
  );
};

export default memo(HistoryHeader);
